import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Grid,
  FormControlLabel,
  Tooltip,
  Select,
  MenuItem,
  ListSubheader,
  InputLabel,
  FormControl,
  Chip,
} from '@mui/material';
import CustomDropdown from '../../../components/CustomDropdownWithPlus';
import SupplierSearchSelectAsync from '../../SupplierSearchSelectAsync';
import CustomDatePicker from '../../CustomDatePicker';
import CustomTextField from '../../CustomTextField';
import PublicIcon from '@mui/icons-material/Public';
import TextArea from '../../TextArea';
import dayjs from 'dayjs';
import ThirdPartyShippingAccount from '../ThirdPartyShippingAccount';
import SearchableAddressDropdown from './SearchableAddressDropdown';
import { labelStyle } from './index.controller';

const supplierSourceToSearchTab = (source) => {
  const normalized = String(source || "").trim().toLowerCase();

  if (normalized === "core-supplier") return "core";
  if (normalized === "asi-supplier") return "all";

  return "core";
};

const searchTabToSupplierSource = (tab) =>
  tab === "all" ? "asi-supplier" : "core-supplier";

const MainShippingForm = ({
  formData,
  selectedSupplierObj,
  handleSelect,
  handleCheckBox,
  handleDateChange,
  handleInputChange,
  handleRadio,
  supplierOptions,
  shippingAccountOptions,
  shippingMethodOptions,
  shipToOptions,
  clientAccount,
  setClientAccounts,
  setSupplierAccounts,
  ourAccounts,
  supplierAccounts,
  addressOptions,
  companyAddressOptions = [],
  openAccountModal,
  closeAccountModal,
  customerId,
  customerDisplayName,
  instanceUniqueId,

  // ✅ Optional: pass down from parent if you have it (AuthContext role, etc.)
  userRole,
}) => {
  // Which "context" did the + button come from?
  // client -> create third-party account for customerId
  // supplier -> create third-party account for supplierId
  const [accountModalContext, setAccountModalContext] = useState({ type: 'customer' }); // 'customer' | 'supplier'
  const [supplierSearchTab, setSupplierSearchTab] = useState(() =>
    supplierSourceToSearchTab(formData?.supplierSource)
  );
  const [supplierMarket, setSupplierMarket] = useState('GLOBAL');

  useEffect(() => {
    const nextTab = supplierSourceToSearchTab(formData?.supplierSource);

    // Only sync when an actual supplier is loaded/selected.
    // This prevents accidentally moving tabs while user is creating a new blank row.
    if ((formData?.supplier || formData?.supplierId) && nextTab !== supplierSearchTab) {
      setSupplierSearchTab(nextTab);
    }
  }, [formData?.supplierSource, formData?.supplier, formData?.supplierId, supplierSearchTab]);

  const supplierName = useMemo(() => {
    return (
      supplierOptions?.find((s) => String(s.id) === String(formData.supplier))?.name ||
      ''
    );
  }, [supplierOptions, formData.supplier]);

  const openCustomerAccountModal = () => {
    if (!customerId) return;
    openAccountModal('customer');
  };
  
  const openSupplierAccountModal = () => {
    if (!formData.supplier) return;
    openAccountModal('supplier');
  };  

  return (
    <>
      <Grid item xs={5}>
        <Box className="field">
<Box className="field">
  <Box sx={{ mb: 1 }}>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        color: 'text.secondary',
        mb: 0.65,
      }}
    >
      Supplier Source
    </Typography>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Supplier source segmented control */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          p: 0.35,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'grey.50',
          minWidth: 0,
        }}
      >
        {[
          { value: 'core', label: 'Internal Supplier' },
          { value: 'all', label: 'Supplier' },
        ].map((option) => {
          const active = supplierSearchTab === option.value;

          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              onClick={() => {
                if (active) return;

                setSupplierSearchTab(option.value);

                handleSelect(
                  {
                    target: { value: '' },
                    supplierSearchMode: option.value,
                    supplierSearchTab: option.value,
                    supplier: null,
                  },
                  'supplier'
                );
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                border: 0,
                px: 1,
                py: 0.7,
                borderRadius: 1.5,
                cursor: active ? 'default' : 'pointer',
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: active ? 700 : 600,
                lineHeight: 1.2,
                color: active ? 'primary.main' : 'text.secondary',
                backgroundColor: active
                  ? 'background.paper'
                  : 'transparent',
                boxShadow: active
                  ? '0 1px 3px rgba(0,0,0,0.08)'
                  : 'none',
                transition: 'all 150ms ease',

                '&:hover': {
                  backgroundColor: active
                    ? 'background.paper'
                    : 'action.hover',
                },

                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                },
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>

      {/* Market selector only for Supplier */}
      {supplierSearchTab === 'all' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Canada suppliers" arrow>
            <Box
              component="button"
              type="button"
              onClick={() => setSupplierMarket('CANALL')}
              sx={{
                width: 30,
                height: 30,
                p: 0,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor:
                  supplierMarket === 'CANALL'
                    ? 'primary.main'
                    : 'divider',
                backgroundColor:
                  supplierMarket === 'CANALL'
                    ? 'primary.main'
                    : 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 150ms ease',

                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <img
                src="https://flagcdn.com/ca.svg"
                alt="Canada"
                style={{
                  width: 17,
                  height: 12,
                  objectFit: 'cover',
                  borderRadius: 2,
                }}
              />
            </Box>
          </Tooltip>

          <Tooltip title="All suppliers" arrow>
            <Box
              component="button"
              type="button"
              onClick={() => setSupplierMarket('GLOBAL')}
              sx={{
                width: 30,
                height: 30,
                p: 0,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor:
                  supplierMarket === 'GLOBAL'
                    ? 'primary.main'
                    : 'divider',
                backgroundColor:
                  supplierMarket === 'GLOBAL'
                    ? 'primary.main'
                    : 'background.paper',
                color:
                  supplierMarket === 'GLOBAL'
                    ? 'primary.contrastText'
                    : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 150ms ease',

                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <PublicIcon sx={{ fontSize: 17 }} />
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  </Box>

  <SupplierSearchSelectAsync
    key={supplierSearchTab}
    instanceUniqueId={instanceUniqueId}
    value={formData.supplier || formData.supplierId || ''}
    selectedSupplierOption={selectedSupplierObj}
    tab={supplierSearchTab}
    searchMode={supplierSearchTab}
    market={supplierMarket}
    onChange={(supplierId) => {
      handleSelect(
        {
          target: { value: supplierId || '' },
          supplierSearchMode: supplierSearchTab,
          supplierSearchTab,
        },
        'supplier'
      );
    }}
    onSelectSupplier={(supplier) => {
      const supplierSource =
        searchTabToSupplierSource(supplierSearchTab);

      handleSelect(
        {
          target: {
            value: supplier?.id || supplier?.Id || '',
          },
          supplier: supplier
            ? {
                ...supplier,
                supplierSource:
                  supplier?.supplierSource || supplierSource,
              }
            : null,
          supplierSearchMode: supplierSearchTab,
          supplierSearchTab,
        },
        'supplier'
      );
    }}
    label={
      supplierSearchTab === 'core'
        ? 'Internal Supplier'
        : 'Supplier'
    }
    showLabel={false}
    placeholder={
      supplierSearchTab === 'core'
        ? 'Search internal suppliers...'
        : 'Search suppliers...'
    }
  />
</Box>

        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          {/*<Box display="flex" alignItems="center">
            <Typography sx={labelStyle}>Sample Order</Typography>
            <Checkbox
              name="sampleOrder"
              checked={formData.sampleOrder}
              onChange={() => handleCheckBox('sampleOrder', formData.sampleOrder)}
              color="primary"
            />
          </Box>*/}

          <Box display="flex" alignItems="center">
            <Typography sx={labelStyle}>Firm</Typography>
            <Checkbox
              name="firm"
              checked={formData.firm}
              onChange={() =>
                handleCheckBox('firm', formData.firm)
              }
              color="primary"
            />
          </Box>
        </Box>

        <Box className="field">
          <CustomDatePicker
            isRequired
            label="In Hands Date"
            value={formData.inHandsDate}
            onChange={(date) =>
              handleDateChange(date, 'inHandsDate')
            }
            minValue={dayjs()}
          />
        </Box>

        <Box
          className="shipping-ship-to-selection"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              ...labelStyle,
              minWidth: '80px',
              mt: 0.5,
            }}
          >
            Ship To
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '80%',
            }}
          >
            {shipToOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                control={
                  <input
                    type="radio"
                    name="shipTo"
                    value={option.name}
                    checked={formData.shipTo === option.id}
                    onChange={() =>
                      handleRadio(option.id, 'shipTo')
                    }
                    style={{
                      width: '50%',
                      minWidth: '100px',
                    }}
                  />
                }
                label={option.name}
                sx={radioStyle}
              />
            ))}
          </Box>
        </Box>

        {(formData.shipTo === 'client' || formData.shipTo === 'elite') && (
          <SearchableAddressDropdown
            name="address"
            label="Address"
            placeholder="Search addresses"
            options={formData.shipTo === 'elite'
              ? [...companyAddressOptions, ...addressOptions.filter((a) => String(a.id) === String(formData.address))]
              : addressOptions}
            selectedValue={formData.address}
            onSelect={(id) => handleSelect({ target: { value: id } }, 'address')}
          />
        )}

        <Box>
          {/*Shipping account options:
          JSON.stringify(shippingAccountOptions)
          Currently selected is: {formData.shippingAccount*/}

          <Box className="field">
            <CustomDropdown
              name="shippingAccount"
              options={shippingAccountOptions}
              handleSelect={handleSelect}
              minWidth={"150px"}
              selectedValue={
                formData.shippingAccount
              }
              placeholder="Select Shipping Account"
              label="Shipping Account"
            />
          </Box>

          {formData.shippingAccount === 'other' && (
            <Box
              className="field"
              sx={{ mt: 1 }}
            >
              <CustomTextField
                name="manualShippingAccount"
                value={
                  formData.manualShippingAccount || ''
                }
                isRequired
                placeholder="Enter shipping account or arrangement"
                label="Other Shipping Account"
                handleChange={handleInputChange}
              />
            </Box>
          )}

          {(formData.shippingAccount === 'our' ||
            formData.shippingAccount === 'client') && (
            <Box className="field">
              <Tooltip
                title={
                  formData.shippingAccount === 'our' &&
                  userRole === 'administrator'
                    ? 'You can manage Our Shipping Accounts in Company Settings > Shipping Accounts.'
                    : ''
                }
                placement="top"
                arrow
                disableHoverListener={
                  !(
                    formData.shippingAccount === 'our' &&
                    userRole === 'administrator'
                  )
                }
              >
                <Box>
                  <CustomDropdown
                    key={`shippingUser-${formData.shippingAccount}`}
                    name="shippingUser"
                    label={
                      formData.shippingAccount === 'our'
                        ? ''
                        : 'Shipping type'
                    }
                    placeholder={
                      formData.shippingAccount === 'our'
                        ? ''
                        : 'Shipping type'
                    }
                    options={
                      formData.shippingAccount === 'our'
                        ? ourAccounts
                        : clientAccount
                    }
                    handleSelect={handleSelect}
                    minWidth="150px"
                    selectedValue={
                      formData.shippingUser
                    }
                    addPlusIcon={
                      formData.shippingAccount === 'client' &&
                      Boolean(customerId)
                    }
                    onPlusClick={
                      openCustomerAccountModal
                    }
                  />
                </Box>
              </Tooltip>

              {/*Our acounts: JSON.stringify(ourAccounts)
              Client accounts: JSON.stringify(clientAccount)
              Supplier accounts: JSON.stringify(supplierAccounts)*/}
            </Box>
          )}

          {formData.shippingAccount ===
            'supplier' && (
            <Box className="field">
              {
                //Supplier's accounts:
                //JSON.stringify(supplierAccounts)
              }

              {
                //VIEWING shippingAccount as 'supplier'
              }

              <CustomDropdown
                key="shippingUser-supplier"
                name="shippingUser"
                label="Supplier account"
                placeholder="Select supplier account"
                options={supplierAccounts}
                handleSelect={handleSelect}
                minWidth="150px"
                selectedValue={
                  formData.shippingUser
                }
                addPlusIcon={Boolean(
                  formData.supplier
                )}
                onPlusClick={
                  openSupplierAccountModal
                }
              />
            </Box>
          )}
        </Box>

        <Box className="field">
          {/*Shipping method:
          JSON.stringify(shippingMethodOptions)
          */}

          <CustomDropdown
            name="shippingMethod"
            options={shippingMethodOptions}
            handleSelect={handleSelect}
            minWidth={"150px"}
            selectedValue={
              formData.shippingMethod
            }
            placeholder="Select Supplier"
            label="Shipping Method"
          />
        </Box>

        <Box className="field">
          <TextArea
            isRequired
            handleInputChange={
              handleInputChange
            }
            value={formData.supplierNotes}
            name="supplierNotes"
            label="Supplier Notes"
          />
        </Box>

        {/*Customer ID: {customerId}
      Supplier info: {JSON.stringify(formData.supplier)}
      Supplier name: {supplierName}
        Customer display name: {customerDisplayName} */}

      </Grid>
    </>
  );
};

const radioStyle = {
  alignItems: 'center',
  fontSize: '14px !important',
  fontFamily: 'Nunito !important',
  color: '#555 !important',
  width: '50%',
  display: 'flex',
  justifyContent: 'space-between',
  whiteSpace: 'noWrap',
};

export default MainShippingForm;